'use client'

/* ─────────────────────────────────────────────────────────────────────────────
   BRAND ASSET SYSTEM (gallery-first — pending approval at /admin/brand-assets)

   Every default/placeholder asset as an SVG React component. The logo is the
   REAL mark extracted from public/logo.png (public/brand/logo-mark*.png/svg)
   — referenced via <image>, never redrawn. No Arabic words are rendered
   decoratively anywhere. Registered in DESIGN_SYSTEM.md §Assets.
───────────────────────────────────────────────────────────────────────────── */

import React, { useId } from 'react'
import { sourceBadgeLabel } from '../../lib/provenance'

/* ── tokens ── */
export const BRAND_GRADS = {
  emerald: ['#13926E', '#0A5C46', '#07332A'],
  gold: ['#B8923B', '#8A6A1C', '#4F3D0E'],
  navy: ['#1E3D6B', '#13284C', '#0A1426'],
  teal: ['#12837B', '#0B5B56', '#06302D'],
  sand: ['#B89C58', '#8C7233', '#564415'],
  midnight: ['#2A3A63', '#172341', '#0A1020'],
} as const
export type BrandVariant = keyof typeof BRAND_GRADS
export const BRAND_VARIANTS = Object.keys(BRAND_GRADS) as BrandVariant[]

export const RATIOS = {
  card: [1600, 1000],
  hero: [2100, 900],
  square: [900, 900],
  og: [1200, 630],
} as const
export type Ratio = keyof typeof RATIOS

/* deterministic variant by slug hash (existing behaviour preserved) */
export function variantForSlug(slug: string | null | undefined): BrandVariant {
  const s = slug || 'masjid'
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return BRAND_VARIANTS[h % BRAND_VARIANTS.length]
}

const GOLD = ['#F2DA8C', '#C9A227']
const LINE = '#FFFFFF'

/* ── scene scaffold: gradient + glow + vignette + grain + REAL logo watermark ── */
function SceneDefs({ id, grad }: { id: string; grad: readonly string[] }) {
  return (
    <defs>
      <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={grad[0]} />
        <stop offset="0.55" stopColor={grad[1]} />
        <stop offset="1" stopColor={grad[2]} />
      </linearGradient>
      <radialGradient id={`${id}-glow`} cx="0.72" cy="0.16" r="0.75">
        <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.16" />
        <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-vig`} cx="0.5" cy="0.46" r="0.9">
        <stop offset="0.62" stopColor="#000000" stopOpacity="0" />
        <stop offset="1" stopColor="#000000" stopOpacity="0.3" />
      </radialGradient>
      <linearGradient id={`${id}-gold`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={GOLD[0]} />
        <stop offset="1" stopColor={GOLD[1]} />
      </linearGradient>
      <filter id={`${id}-grain`} x="0" y="0" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
    </defs>
  )
}

/* the REAL mark (white knockout) as a quiet watermark — 8–10% opacity, bottom-right */
function LogoWatermark({ w, h, size = 0.16 }: { w: number; h: number; size?: number }) {
  const mw = w * size
  const mh = mw * (622 / 826)
  return (
    <image
      href="/brand/logo-mark-white.png"
      x={w - mw - w * 0.035}
      y={h - mh - w * 0.03}
      width={mw}
      height={mh}
      opacity={0.09}
      preserveAspectRatio="xMidYMid meet"
    />
  )
}

function Scene({
  ratio, variant, children, watermark = true, className,
}: {
  ratio: Ratio
  variant: BrandVariant
  children?: (w: number, h: number, id: string) => React.ReactNode
  watermark?: boolean
  className?: string
}) {
  const rawId = useId()
  const id = rawId.replace(/[^a-zA-Z0-9]/g, '')
  const [w, h] = RATIOS[ratio]
  const grad = BRAND_GRADS[variant]
  // preserveAspectRatio 'slice' = true cover: fills the (often fixed-height) card cover box
  // cleanly instead of letterboxing/distorting under the container's object-fit:cover.
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" className={className} style={{ display: 'block', width: '100%', height: '100%' }} role="img" aria-label="">
      <SceneDefs id={id} grad={grad} />
      <rect width={w} height={h} fill={`url(#${id}-bg)`} />
      <rect width={w} height={h} fill={`url(#${id}-glow)`} />
      <rect width={w} height={h} fill={`url(#${id}-vig)`} />
      {children?.(w, h, id)}
      {watermark && <LogoWatermark w={w} h={h} />}
      <rect width={w} height={h} filter={`url(#${id}-grain)`} opacity="0.05" />
    </svg>
  )
}

/* ── 1) MASJID PLACEHOLDER — refined arcade + dome + minaret line-art ── */
function MosqueArt({ w, h, shift = 0 }: { w: number; h: number; shift?: number }) {
  const cx = w * (0.5 + shift)
  const base = h * 0.78
  const domeR = Math.min(w, h) * 0.27
  const arch = (x: number, aw: number, ah: number) =>
    `M ${x - aw / 2} ${base} V ${base - ah * 0.55} Q ${x - aw / 2} ${base - ah} ${x} ${base - ah} Q ${x + aw / 2} ${base - ah} ${x + aw / 2} ${base - ah * 0.55} V ${base}`
  const arcade: string[] = []
  const aw = w * 0.052
  for (let i = -8; i <= 8; i++) {
    const x = cx + i * aw * 1.45
    if (x > -aw && x < w + aw && Math.abs(x - cx) > domeR * 0.92) arcade.push(arch(x, aw, h * 0.085))
  }
  return (
    <g fill="none" stroke={LINE} strokeOpacity="0.26" strokeWidth={Math.max(2, w * 0.0014)} strokeLinecap="round" strokeLinejoin="round">
      {/* ground lines */}
      <line x1={w * 0.04} y1={base} x2={w * 0.96} y2={base} />
      <line x1={w * 0.1} y1={base + h * 0.045} x2={w * 0.9} y2={base + h * 0.045} strokeOpacity="0.12" />
      {/* dome */}
      <path d={`M ${cx - domeR} ${base} C ${cx - domeR} ${base - domeR * 1.18} ${cx - domeR * 0.42} ${base - domeR * 1.52} ${cx} ${base - domeR * 1.62} C ${cx + domeR * 0.42} ${base - domeR * 1.52} ${cx + domeR} ${base - domeR * 1.18} ${cx + domeR} ${base}`} />
      <path d={`M ${cx - domeR * 0.62} ${base} C ${cx - domeR * 0.62} ${base - domeR * 0.95} ${cx - domeR * 0.2} ${base - domeR * 1.25} ${cx} ${base - domeR * 1.33}`} strokeOpacity="0.13" />
      {/* doorway arch */}
      <path d={arch(cx, domeR * 0.5, h * 0.14)} />
      {/* mast + crescent */}
      <line x1={cx} y1={base - domeR * 1.62} x2={cx} y2={base - domeR * 1.78} />
      <path d={`M ${cx + domeR * 0.07} ${base - domeR * 1.96} A ${domeR * 0.1} ${domeR * 0.1} 0 1 0 ${cx + domeR * 0.07} ${base - domeR * 1.8} A ${domeR * 0.08} ${domeR * 0.08} 0 1 1 ${cx + domeR * 0.07} ${base - domeR * 1.96}`} stroke="none" fill={LINE} fillOpacity="0.3" />
      {/* minarets */}
      {[cx - domeR * 1.5, cx + domeR * 1.5].map((mx, i) => (
        <g key={i}>
          <line x1={mx - domeR * 0.07} y1={base} x2={mx - domeR * 0.05} y2={base - domeR * 1.95} />
          <line x1={mx + domeR * 0.07} y1={base} x2={mx + domeR * 0.05} y2={base - domeR * 1.95} />
          <path d={`M ${mx - domeR * 0.09} ${base - domeR * 1.95} Q ${mx} ${base - domeR * 2.22} ${mx + domeR * 0.09} ${base - domeR * 1.95}`} />
          <line x1={mx - domeR * 0.11} y1={base - domeR * 1.4} x2={mx + domeR * 0.11} y2={base - domeR * 1.4} strokeOpacity="0.16" />
        </g>
      ))}
      {/* arcade */}
      {arcade.map((d, i) => <path key={i} d={d} strokeOpacity="0.16" />)}
    </g>
  )
}

export function MasjidPlaceholder({ variant, ratio = 'card', slug }: { variant?: BrandVariant; ratio?: Ratio; slug?: string }) {
  const v = variant || variantForSlug(slug)
  /* hero ratio: art shifts right so headline text overlays the left half */
  const shift = ratio === 'hero' ? 0.24 : 0
  return (
    <Scene ratio={ratio} variant={v}>
      {(w, h) => <MosqueArt w={w} h={h} shift={shift} />}
    </Scene>
  )
}

/* ── 2) CAMPAIGN PLACEHOLDERS by category ── */
export const CAMPAIGN_CATEGORIES = ['construction', 'renovation', 'education', 'water', 'medical', 'food', 'waqf', 'general'] as const
export type CampaignCategory = (typeof CAMPAIGN_CATEGORIES)[number]

function star8(cx: number, cy: number, R: number) {
  const pts: string[] = []
  for (let i = 0; i < 16; i++) {
    const a = (Math.PI * i) / 8 - Math.PI / 2
    const r = i % 2 === 0 ? R : R * 0.42
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`)
  }
  return pts.join(' ')
}

/* iconography drawn in a 200×200 box centered at 0,0 */
function CampaignIcon({ category, id }: { category: CampaignCategory; id: string }) {
  const gold = `url(#${id}-gold)`
  switch (category) {
    case 'construction':
      return (<g>
        <path d="M -70 70 V -10 Q -70 -62 0 -74 Q 70 -62 70 -10 V 70" />
        <line x1="-92" y1="70" x2="92" y2="70" />
        <line x1="-46" y1="70" x2="-46" y2="-30" strokeOpacity="0.5" />
        <line x1="46" y1="70" x2="46" y2="-30" strokeOpacity="0.5" />
        <line x1="-46" y1="20" x2="46" y2="20" strokeOpacity="0.5" />
        <line x1="-46" y1="-30" x2="46" y2="20" strokeOpacity="0.35" />
        <line x1="46" y1="-30" x2="-46" y2="20" strokeOpacity="0.35" />
        <line x1="0" y1="-74" x2="0" y2="-90" stroke={gold} />
        <circle cx="0" cy="-96" r="5" fill={gold} stroke="none" />
      </g>)
    case 'renovation':
      return (<g>
        <path d="M -60 64 V 0 Q -60 -50 0 -60 Q 60 -50 60 0 V 64" strokeOpacity="0.5" />
        <path d="M -34 38 L 18 -14" stroke={gold} strokeWidth="7" />
        <path d="M 18 -14 L 40 -36 A 18 18 0 1 0 14 -40 L 8 -24" stroke={gold} />
        <path d="M -34 38 l -12 24 l 24 -12 z" fill={gold} stroke="none" />
      </g>)
    case 'education':
      return (<g>
        <path d="M 0 -34 C -26 -52 -62 -52 -84 -42 V 36 C -62 26 -26 26 0 44 C 26 26 62 26 84 36 V -42 C 62 -52 26 -52 0 -34 Z" />
        <line x1="0" y1="-34" x2="0" y2="44" />
        <path d="M -64 -22 Q -32 -30 -14 -20" strokeOpacity="0.5" />
        <path d="M -64 0 Q -32 -8 -14 2" strokeOpacity="0.5" />
        <path d="M 36 -36 L 78 -78 L 90 -66 L 48 -24 L 32 -20 Z" stroke={gold} fill="none" />
      </g>)
    case 'water':
      return (<g>
        <path d="M -50 60 V 10 Q -50 -10 -30 -14 H 30 Q 50 -10 50 10 V 60" />
        <line x1="-66" y1="60" x2="66" y2="60" />
        <path d="M -42 -14 L 0 -52 L 42 -14" />
        <line x1="0" y1="-52" x2="0" y2="-24" strokeOpacity="0.5" />
        <path d="M 0 4 C -12 22 -16 30 -16 38 A 16 16 0 0 0 16 38 C 16 30 12 22 0 4 Z" stroke={gold} fill="none" />
      </g>)
    case 'medical':
      return (<g>
        <path d="M 16 -56 A 44 44 0 1 0 16 32 A 35 35 0 1 1 16 -56" />
        <g stroke={gold}>
          <line x1="44" y1="-12" x2="44" y2="36" strokeWidth="7" />
          <line x1="20" y1="12" x2="68" y2="12" strokeWidth="7" />
        </g>
      </g>)
    case 'food':
      return (<g>
        <path d="M -60 8 A 60 38 0 0 0 60 8 Z" />
        <line x1="-72" y1="8" x2="72" y2="8" strokeOpacity="0.5" />
        <path d="M 34 -10 V -64" stroke={gold} />
        {[-50, -38, -26].map((y, i) => (
          <g key={i} stroke={gold} strokeOpacity="0.9">
            <path d={`M 34 ${y} q -12 -7 -13 -17 q 12 1 13 17`} />
            <path d={`M 34 ${y} q 12 -7 13 -17 q -12 1 -13 17`} />
          </g>
        ))}
      </g>)
    case 'waqf':
      return (<g>
        <path d="M 0 -72 L 58 -52 V 0 C 58 36 30 58 0 70 C -30 58 -58 36 -58 0 V -52 Z" />
        <rect x="-26" y="-34" width="52" height="58" rx="6" strokeOpacity="0.6" />
        <line x1="-14" y1="-18" x2="14" y2="-18" strokeOpacity="0.6" />
        <line x1="-14" y1="-4" x2="14" y2="-4" strokeOpacity="0.6" />
        <line x1="-14" y1="10" x2="4" y2="10" strokeOpacity="0.6" />
        <circle cx="0" cy="-52" r="4" fill={gold} stroke="none" />
      </g>)
    default:
      return (<g>
        <polygon points={star8(0, 0, 66)} />
        <polygon points={star8(0, 0, 38)} strokeOpacity="0.45" />
        <circle cx="0" cy="0" r="7" fill={gold} stroke="none" />
      </g>)
  }
}

const CATEGORY_VARIANT: Record<CampaignCategory, BrandVariant> = {
  construction: 'navy', renovation: 'teal', education: 'emerald', water: 'teal',
  medical: 'emerald', food: 'sand', waqf: 'midnight', general: 'gold',
}

export function CampaignPlaceholder({ category = 'general', ratio = 'card' }: { category?: CampaignCategory; ratio?: Extract<Ratio, 'card' | 'og'> }) {
  return (
    <Scene ratio={ratio} variant={CATEGORY_VARIANT[category]}>
      {(w, h, id) => (
        <g transform={`translate(${w / 2}, ${h * 0.46}) scale(${Math.min(w, h) / 320})`} fill="none" stroke={LINE} strokeOpacity="0.5" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <CampaignIcon category={category} id={id} />
        </g>
      )}
    </Scene>
  )
}

/* ── 3) ENTITY PLACEHOLDERS ── */
export const BUSINESS_CATEGORIES = ['food', 'retail', 'services', 'health', 'education'] as const
export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number]

function BusinessIcon({ category }: { category: BusinessCategory }) {
  switch (category) {
    case 'food': return (<g><path d="M -44 6 A 44 28 0 0 0 44 6 Z" /><line x1="-54" y1="6" x2="54" y2="6" /><path d="M -16 -14 q 0 -14 8 -20" strokeOpacity="0.55" /><path d="M 8 -14 q 0 -14 8 -20" strokeOpacity="0.55" /></g>)
    case 'retail': return (<g><path d="M -42 44 V -8 H 42 V 44" /><path d="M -50 -8 L -42 -34 H 42 L 50 -8" /><path d="M -50 -8 H 50" /><path d="M -10 44 V 12 H 10 V 44" strokeOpacity="0.6" /></g>)
    case 'services': return (<g><circle cx="0" cy="0" r="16" /><path d="M 0 -42 V -28 M 0 28 V 42 M -42 0 H -28 M 28 0 H 42 M -30 -30 L -20 -20 M 20 20 L 30 30 M 30 -30 L 20 -20 M -20 20 L -30 30" /></g>)
    case 'health': return (<g><line x1="0" y1="-34" x2="0" y2="34" strokeWidth="9" /><line x1="-34" y1="0" x2="34" y2="0" strokeWidth="9" /></g>)
    default: return (<g><path d="M 0 -26 L -46 -8 L 0 10 L 46 -8 Z" /><path d="M -26 0 V 22 Q 0 36 26 22 V 0" /><line x1="40" y1="-4" x2="40" y2="24" strokeOpacity="0.6" /></g>)
  }
}

export function BusinessPlaceholder({ category = 'retail', ratio = 'card' }: { category?: BusinessCategory; ratio?: Ratio }) {
  return (
    <Scene ratio={ratio} variant="navy">
      {(w, h) => (
        <g transform={`translate(${w / 2}, ${h * 0.46})`}>
          {/* rounded glass tile */}
          <rect x={-Math.min(w, h) * 0.19} y={-Math.min(w, h) * 0.19} width={Math.min(w, h) * 0.38} height={Math.min(w, h) * 0.38} rx={Math.min(w, h) * 0.07}
            fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.22)" strokeWidth="2.5" />
          <g transform={`scale(${Math.min(w, h) / 480})`} fill="none" stroke={LINE} strokeOpacity="0.6" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
            <BusinessIcon category={category} />
          </g>
        </g>
      )}
    </Scene>
  )
}

export function EventPlaceholder({ ratio = 'card' }: { ratio?: Ratio }) {
  return (
    <Scene ratio={ratio} variant="midnight">
      {(w, h, id) => (
        <g transform={`translate(${w / 2}, ${h * 0.47}) scale(${Math.min(w, h) / 330})`} fill="none" stroke={LINE} strokeOpacity="0.45" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <rect x="-78" y="-58" width="116" height="116" rx="14" />
          <line x1="-78" y1="-26" x2="38" y2="-26" />
          <line x1="-52" y1="-72" x2="-52" y2="-46" />
          <line x1="12" y1="-72" x2="12" y2="-46" />
          <line x1="-56" y1="0" x2="-36" y2="0" strokeOpacity="0.55" />
          <line x1="-56" y1="24" x2="-36" y2="24" strokeOpacity="0.55" />
          {/* lantern */}
          <g stroke={`url(#${id}-gold)`}>
            <line x1="62" y1="-72" x2="62" y2="-56" />
            <path d="M 48 -56 H 76 L 82 -20 Q 62 -6 42 -20 Z" />
            <path d="M 52 -56 L 62 -20 M 72 -56 L 62 -20" strokeOpacity="0.6" />
            <line x1="62" y1="-6" x2="62" y2="4" />
          </g>
        </g>
      )}
    </Scene>
  )
}

export const JOB_ROLES = ['imam', 'teacher', 'general'] as const
export type JobRole = (typeof JOB_ROLES)[number]

export function JobPlaceholder({ role = 'general', ratio = 'card' }: { role?: JobRole; ratio?: Ratio }) {
  return (
    <Scene ratio={ratio} variant="teal">
      {(w, h, id) => (
        <g transform={`translate(${w / 2}, ${h * 0.47}) scale(${Math.min(w, h) / 330})`} fill="none" stroke={LINE} strokeOpacity="0.45" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          {role === 'imam' && (<g>
            {/* mihrab + minbar steps */}
            <path d="M -64 58 V -6 Q -64 -54 -22 -62 Q 20 -54 20 -6 V 58" />
            <path d="M -50 58 V -2 Q -50 -40 -22 -47 Q 6 -40 6 -2 V 58" strokeOpacity="0.3" />
            <path d="M 34 58 V 36 H 50 V 14 H 66 V -8 H 82 V 58 Z" stroke={`url(#${id}-gold)`} />
            <line x1="-80" y1="58" x2="92" y2="58" />
          </g>)}
          {role === 'teacher' && (<g>
            <path d="M 0 -22 C -22 -38 -54 -38 -72 -30 V 34 C -54 26 -22 26 0 40 C 22 26 54 26 72 34 V -30 C 54 -38 22 -38 0 -22 Z" />
            <line x1="0" y1="-22" x2="0" y2="40" />
            <path d="M 30 -34 L 64 -68 L 74 -58 L 40 -24 L 26 -20 Z" stroke={`url(#${id}-gold)`} />
          </g>)}
          {role === 'general' && (<g>
            <rect x="-58" y="-26" width="116" height="76" rx="12" />
            <path d="M -20 -26 V -38 Q -20 -46 -12 -46 H 12 Q 20 -46 20 -38 V -26" />
            <line x1="-58" y1="6" x2="58" y2="6" strokeOpacity="0.5" />
            <rect x="-9" y="0" width="18" height="13" rx="3" stroke={`url(#${id}-gold)`} />
          </g>)}
        </g>
      )}
    </Scene>
  )
}

export function ArticlePlaceholder({ accent = '#14B8A6', ratio = 'card' }: { accent?: string; ratio?: Ratio }) {
  return (
    <Scene ratio={ratio} variant="sand">
      {(w, h) => (
        <g transform={`translate(${w / 2}, ${h * 0.47}) scale(${Math.min(w, h) / 330})`} fill="none" stroke={LINE} strokeOpacity="0.45" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          {/* scroll */}
          <path d="M -64 -52 H 44 Q 58 -52 58 -38 V 44 Q 58 58 44 58 H -50" />
          <path d="M -64 -52 Q -78 -52 -78 -38 Q -78 -24 -64 -24 Q -50 -24 -50 -38 V 44 Q -50 58 -64 58 Q -78 58 -78 44" strokeOpacity="0.55" />
          <line x1="-34" y1="-26" x2="36" y2="-26" strokeOpacity="0.5" />
          <line x1="-34" y1="-6" x2="36" y2="-6" strokeOpacity="0.5" />
          <line x1="-34" y1="14" x2="16" y2="14" strokeOpacity="0.5" />
          {/* qalam with category accent */}
          <path d="M 18 46 L 66 -2 L 78 10 L 30 58 L 12 64 Z" stroke={accent} />
        </g>
      )}
    </Scene>
  )
}

/* ── user avatars: initials in Poppins on deterministic two-tone gradient ── */
const AVATAR_DUOS: Array<[string, string]> = [
  ['#13926E', '#0A5C46'], ['#1E3D6B', '#0B1426'], ['#B8923B', '#6B5320'],
  ['#12837B', '#06302D'], ['#2A3A63', '#0A1020'], ['#0E7C61', '#14B8A6'],
]

export function initialsOf(name: string | null | undefined): string {
  const parts = String(name || '?').trim().split(/\s+/)
  const a = parts[0]?.charAt(0) || '?'
  const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''
  return (a + b).toUpperCase()
}

export function BrandAvatar({ name, size = 44 }: { name?: string | null; size?: number }) {
  const rawId = useId()
  const id = rawId.replace(/[^a-zA-Z0-9]/g, '')
  const s = String(name || 'user')
  let hsh = 0
  for (let i = 0; i < s.length; i++) hsh = (hsh * 31 + s.charCodeAt(i)) >>> 0
  const [c1, c2] = AVATAR_DUOS[hsh % AVATAR_DUOS.length]
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={s}>
      <defs>
        <linearGradient id={`${id}-av`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={c1} /><stop offset="1" stopColor={c2} />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="50" fill={`url(#${id}-av)`} />
      <text x="50" y="50" dy="0.36em" textAnchor="middle" fontFamily="Poppins, sans-serif" fontWeight="600" fontSize="38" fill="#FFFFFF" fillOpacity="0.92">{initialsOf(name)}</text>
    </svg>
  )
}

/* ── 4) PAGE HERO BACKDROPS — navy base + layered glow + role motif at 3–5% ── */
export const HERO_ROLES = ['directory', 'masjid', 'give', 'waqf', 'community', 'jobs', 'business', 'articles', 'pricing'] as const
export type HeroRole = (typeof HERO_ROLES)[number]

function HeroMotif({ role, w, h }: { role: HeroRole; w: number; h: number }) {
  const g = (children: React.ReactNode, opacity = 0.045) => (
    <g fill="none" stroke={LINE} strokeOpacity={opacity} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{children}</g>
  )
  switch (role) {
    case 'directory': {
      /* dot-matrix map fragment */
      const dots: React.ReactNode[] = []
      for (let y = 0; y < 9; y++) for (let x = 0; x < 16; x++) {
        const px = w * 0.45 + x * 38; const py = h * 0.18 + y * 38
        if ((x * 7 + y * 13) % 5 !== 0) dots.push(<circle key={`${x}-${y}`} cx={px} cy={py} r={3.2} fill={LINE} opacity={0.05} stroke="none" />)
      }
      return <g>{dots}</g>
    }
    case 'masjid':
      return g(Array.from({ length: 8 }).map((_, i) => (
        <path key={i} d={`M ${w * 0.42 + i * 105} ${h} V ${h - 130} Q ${w * 0.42 + i * 105 + 38} ${h - 195} ${w * 0.42 + i * 105 + 76} ${h - 130} V ${h}`} />
      )))
    case 'give': {
      const stars: React.ReactNode[] = []
      for (let r = 0; r < 3; r++) for (let c = 0; c < 6; c++) {
        stars.push(<polygon key={`${r}-${c}`} points={star8(w * 0.45 + c * 150 + (r % 2) * 75, h * 0.2 + r * 150, 42)} />)
      }
      return g(stars, 0.04)
    }
    case 'waqf':
      return g(<>
        <path d={`M ${w * 0.72} ${h * 0.16} l 110 38 v 96 c 0 66 -52 106 -110 128 c -58 -22 -110 -62 -110 -128 v -96 z`} />
        {Array.from({ length: 5 }).map((_, i) => <line key={i} x1={w * 0.64} y1={h * 0.32 + i * 30} x2={w * 0.8} y2={h * 0.32 + i * 30} />)}
      </>)
    case 'community': {
      const nodes = [[0.5, 0.3], [0.62, 0.55], [0.74, 0.25], [0.84, 0.5], [0.68, 0.8], [0.9, 0.78], [0.56, 0.72]] as const
      return g(<>
        {nodes.map(([nx, ny], i) => <circle key={i} cx={w * nx} cy={h * ny} r="6" fill={LINE} opacity="0.05" stroke="none" />)}
        {nodes.slice(1).map(([nx, ny], i) => <path key={i} d={`M ${w * nodes[i][0]} ${h * nodes[i][1]} Q ${w * (nodes[i][0] + nx) / 2} ${h * ((nodes[i][1] + ny) / 2 - 0.12)} ${w * nx} ${h * ny}`} />)}
      </>)
    }
    case 'jobs':
      return g(Array.from({ length: 5 }).map((_, i) => {
        const x = w * 0.5 + i * 170
        return <g key={i}><line x1={x} y1={h} x2={x + 14} y2={h * 0.3} /><line x1={x + 56} y1={h} x2={x + 42} y2={h * 0.3} /><path d={`M ${x + 8} ${h * 0.3} Q ${x + 28} ${h * 0.18} ${x + 48} ${h * 0.3}`} /><circle cx={x + 28} cy={h * 0.15} r="4" fill={LINE} opacity="0.05" stroke="none" /></g>
      }))
    case 'business':
      return g(Array.from({ length: 7 }).map((_, i) => {
        const x = w * 0.44 + i * 130
        return <path key={i} d={`M ${x} ${h} V ${h * 0.55} Q ${x} ${h * 0.36} ${x + 50} ${h * 0.33} Q ${x + 100} ${h * 0.36} ${x + 100} ${h * 0.55} V ${h}`} />
      }))
    case 'articles':
      /* abstract calligraphy-like swashes — purely geometric, no letters or words */
      return g(<>
        <path d={`M ${w * 0.45} ${h * 0.62} C ${w * 0.55} ${h * 0.2} ${w * 0.68} ${h * 0.95} ${w * 0.8} ${h * 0.45} S ${w * 0.95} ${h * 0.3} ${w * 0.99} ${h * 0.5}`} strokeWidth="7" />
        <path d={`M ${w * 0.5} ${h * 0.8} C ${w * 0.62} ${h * 0.5} ${w * 0.72} ${h * 1.05} ${w * 0.86} ${h * 0.68}`} strokeWidth="4" />
        <circle cx={w * 0.83} cy={h * 0.32} r="5" fill={LINE} opacity="0.05" stroke="none" />
        <circle cx={w * 0.62} cy={h * 0.38} r="4" fill={LINE} opacity="0.04" stroke="none" />
      </>)
    case 'pricing': {
      /* circuit-meets-girih grid */
      const els: React.ReactNode[] = []
      for (let r = 0; r < 3; r++) for (let c = 0; c < 5; c++) {
        const x = w * 0.48 + c * 170; const y = h * 0.18 + r * 170
        els.push(<polygon key={`s${r}-${c}`} points={star8(x, y, 26)} />)
        if (c < 4) els.push(<line key={`l${r}-${c}`} x1={x + 26} y1={y} x2={x + 144} y2={y} />)
        if (r < 2) els.push(<line key={`v${r}-${c}`} x1={x} y1={y + 26} x2={x} y2={y + 144} />)
        els.push(<circle key={`n${r}-${c}`} cx={x + 85} cy={y} r="3.5" fill={LINE} opacity="0.05" stroke="none" />)
      }
      return g(els, 0.04)
    }
  }
}

export function HeroBackdrop({ role, height = 280 }: { role: HeroRole; height?: number | string }) {
  const rawId = useId()
  const id = rawId.replace(/[^a-zA-Z0-9]/g, '')
  const w = 2100; const h = 900
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid slice" style={{ display: 'block', width: '100%', height }} role="img" aria-label="">
      <defs>
        <radialGradient id={`${id}-g1`} cx="0.2" cy="0.3" r="0.6">
          <stop offset="0" stopColor="#0E7C61" stopOpacity="0.16" /><stop offset="1" stopColor="#0E7C61" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-g2`} cx="0.86" cy="0.9" r="0.55">
          <stop offset="0" stopColor="#D4AF37" stopOpacity="0.07" /><stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-base`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#101A38" /><stop offset="1" stopColor="#0B132B" />
        </linearGradient>
      </defs>
      <rect width={w} height={h} fill={`url(#${id}-base)`} />
      <rect width={w} height={h} fill={`url(#${id}-g1)`} />
      <rect width={w} height={h} fill={`url(#${id}-g2)`} />
      <HeroMotif role={role} w={w} h={h} />
    </svg>
  )
}

/* ── 5) DEFAULT OG TEMPLATES (SVG previews; rasterized via @vercel/og on apply) ── */
export function OgTemplate({ section, headline, sub }: { section: string; headline: string; sub?: string }) {
  return (
    <Scene ratio="og" variant="navy" watermark={false}>
      {(w, h, id) => (
        <g>
          <HeroLikeMotifForOg w={w} h={h} />
          <image href="/brand/logo-mark-white.png" x={64} y={56} width={120} height={120 * (622 / 826)} opacity={0.95} />
          <text x={64} y={h * 0.52} fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="64" fill="#F6F4EE">{headline}</text>
          {sub && <text x={64} y={h * 0.52 + 56} fontFamily="Inter, sans-serif" fontWeight="400" fontSize="26" fill="rgba(246,244,238,0.72)">{sub}</text>}
          <text x={64} y={h - 56} fontFamily="JetBrains Mono, monospace" fontSize="20" letterSpacing="4" fill="#2DD4BF">{section.toUpperCase()} · MASJIDS.IN</text>
          <rect x={0} y={0} width={w} height={8} fill={`url(#${id}-gold)`} opacity="0.9" />
        </g>
      )}
    </Scene>
  )
}

function HeroLikeMotifForOg({ w, h }: { w: number; h: number }) {
  return (
    <g fill="none" stroke={LINE} strokeOpacity="0.05" strokeWidth="2.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <path key={i} d={`M ${w * 0.55 + i * 110} ${h} V ${h - 120} Q ${w * 0.55 + i * 110 + 40} ${h - 185} ${w * 0.55 + i * 110 + 80} ${h - 120} V ${h}`} />
      ))}
    </g>
  )
}

/* dynamic masjid OG preview (name + city + badge over the masjid's variant) */
export function MasjidOgPreview({ name, city, verified, slug, importSource }: { name: string; city: string; verified?: boolean; slug?: string; importSource?: string | null }) {
  const v = variantForSlug(slug || name)
  // Honest origin badge for the unverified state. Community/null → plain "LISTED" (no
  // redundant "· LISTED"); OSM → "LISTED · OSM"; google_places → "LISTED · GOOGLE".
  const src = sourceBadgeLabel(importSource)
  const listedLabel = src.short === 'LISTED' ? 'LISTED' : `LISTED · ${src.short}`
  return (
    <Scene ratio="og" variant={v}>
      {(w, h) => (
        <g>
          <g opacity="0.5"><MosqueArt w={w} h={h} shift={0.22} /></g>
          <rect x={48} y={h * 0.3} width={10} height={h * 0.32} rx={5} fill="#2DD4BF" opacity="0.9" />
          <text x={84} y={h * 0.45} fontFamily="Poppins, sans-serif" fontWeight="700" fontSize="56" fill="#F6F4EE">{name}</text>
          <text x={84} y={h * 0.45 + 48} fontFamily="Inter, sans-serif" fontSize="26" fill="rgba(246,244,238,0.75)">{city}</text>
          <g transform={`translate(84, ${h * 0.45 + 84})`}>
            <rect width={verified ? 200 : 220} height="44" rx="22" fill={verified ? 'rgba(20,184,166,0.16)' : 'rgba(255,255,255,0.1)'} stroke={verified ? 'rgba(20,184,166,0.5)' : 'rgba(255,255,255,0.25)'} strokeWidth="1.5" />
            <text x={verified ? 100 : 110} y="29" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="17" letterSpacing="2" fill={verified ? '#2DD4BF' : 'rgba(246,244,238,0.8)'}>
              {verified ? 'VERIFIED MASJID' : listedLabel}
            </text>
          </g>
        </g>
      )}
    </Scene>
  )
}

/* ── 6) EMPTY-STATE SPOT ILLUSTRATIONS — emerald line art on transparent ── */
export const EMPTY_KINDS = ['no-masjids', 'no-campaigns', 'no-posts', 'no-announcements', 'no-results', 'claim'] as const
export type EmptyKind = (typeof EMPTY_KINDS)[number]

export function EmptyArt({ kind, size = 150 }: { kind: EmptyKind; size?: number }) {
  const E = '#0E7C61'
  const inner = (() => {
    switch (kind) {
      case 'no-masjids': return (<g>
        <path d="M 30 110 V 78 Q 30 46 70 38 Q 110 46 110 78 V 110" />
        <line x1="16" y1="110" x2="124" y2="110" />
        <path d="M 62 110 V 92 Q 62 82 70 82 Q 78 82 78 92 V 110" strokeOpacity="0.55" />
        <circle cx="128" cy="58" r="26" />
        <line x1="146" y1="78" x2="166" y2="98" strokeWidth="7" />
      </g>)
      case 'no-campaigns': return (<g>
        <path d="M 85 52 C 76 36 52 36 46 53 C 40 70 62 86 85 104 C 108 86 130 70 124 53 C 118 36 94 36 85 52 Z" />
        <path d="M 30 116 Q 85 146 140 116" strokeOpacity="0.55" />
        <circle cx="30" cy="116" r="5" fill={E} stroke="none" opacity="0.8" />
        <circle cx="140" cy="116" r="5" fill={E} stroke="none" opacity="0.8" />
      </g>)
      case 'no-posts': return (<g>
        <rect x="24" y="34" width="86" height="58" rx="14" />
        <path d="M 46 92 V 108 L 64 92" />
        <line x1="44" y1="56" x2="90" y2="56" strokeOpacity="0.55" />
        <line x1="44" y1="70" x2="76" y2="70" strokeOpacity="0.55" />
        <rect x="86" y="66" width="62" height="44" rx="12" strokeOpacity="0.7" />
        <path d="M 126 110 V 122 L 112 110" strokeOpacity="0.7" />
      </g>)
      case 'no-announcements': return (<g>
        <path d="M 38 70 L 104 42 V 102 L 38 86 Z" />
        <path d="M 38 70 H 26 Q 18 70 18 78 Q 18 86 26 86 H 38" />
        <path d="M 40 86 L 46 110 H 60 L 54 87" strokeOpacity="0.7" />
        <path d="M 118 56 Q 128 72 118 88" strokeOpacity="0.6" />
        <path d="M 132 48 Q 148 72 132 96" strokeOpacity="0.4" />
      </g>)
      case 'no-results': return (<g>
        <circle cx="70" cy="64" r="34" />
        <line x1="94" y1="90" x2="122" y2="118" strokeWidth="7" />
        <line x1="58" y1="56" x2="82" y2="74" strokeOpacity="0.5" />
        <line x1="82" y1="56" x2="58" y2="74" strokeOpacity="0.5" />
        <circle cx="128" cy="40" r="3.5" fill={E} stroke="none" opacity="0.5" />
        <circle cx="140" cy="56" r="2.5" fill={E} stroke="none" opacity="0.35" />
      </g>)
      case 'claim': return (<g>
        <path d="M 36 112 V 82 Q 36 56 68 50 Q 100 56 100 82 V 112" />
        <line x1="24" y1="112" x2="112" y2="112" />
        <line x1="120" y1="112" x2="120" y2="36" />
        <path d="M 120 36 H 156 L 146 48 L 156 60 H 120" fill="rgba(14,124,97,0.12)" />
      </g>)
    }
  })()
  return (
    <svg width={size} height={size * 0.84} viewBox="0 0 176 148" fill="none" stroke={E} strokeOpacity="0.75" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="">
      {inner}
    </svg>
  )
}
